import React, { useState } from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  TouchableOpacity,
} from 'react-native';
import { useDispatch } from 'react-redux';
import Dropzone from 'react-dropzone';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TextField,
  Button as MuiButton,
  CircularProgress,
  Typography,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import DeleteIcon from '@material-ui/icons/Delete';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import getPlatformType from '../../../../../helpers/helpers';
import { tenantActions } from '../../../redux/tenant.actions';
import { showErrorToast, showSuccesToast } from '../../helpers';
import { Button } from '../../atoms/button';
import variables from '../../atoms/variables.json';

import CertList from './certlist';

const layoutType = getPlatformType();

const baseDropzoneStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px 15px',
  borderWidth: 2,
  borderRadius: 10,
  borderColor: '#CBD5E1',
  borderStyle: 'dashed',
  backgroundColor: '#F8FAFC',
  color: '#64748B',
  outline: 'none',
  minHeight: 110,
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
};

const OtherDocuments = ({ flowId, open_flow_certificates, handleDelete, onUploaded }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [documentName, setDocumentName] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [certificateUrl, setCertificateUrl] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const documents = (open_flow_certificates || []).filter(
    (certificate) =>
      certificate.certificate_type !== 'certificates_gas' &&
      certificate.certificate_type !== 'certificates_epc' &&
      certificate.certificate_type !== 'certificates_pat' &&
      certificate.certificate_type !== 'certificates_eicr' &&
      certificate.certificate_type !== 'licensing'
  );

  const handleClose = () => {
    setOpen(false);
    setSelectedItem(null);
    setDocumentName('');
    setUploadedFile(null);
    setCertificateUrl('');
    setErrors({});
    setIsUploading(false);
    setIsSubmitting(false);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setDocumentName(item.certificate_type || '');
    setCertificateUrl(item.certificate_url || '');
    setUploadedFile({
      name: item.certificate_type || 'Document',
      preview: item.certificate_url,
    });
    setErrors({});
    setOpen(true);
  };

  const onDropFile = (acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      const previewUrl = URL.createObjectURL(file);
      Object.assign(file, { preview: previewUrl });
      setUploadedFile(file);

      // Auto-populate document name if empty and not editing with existing name
      if (!documentName.trim()) {
        const rawName = file.name ? file.name.replace(/\.[^/.]+$/, '') : '';
        setDocumentName(rawName.replace(/[_-]/g, ' '));
      }

      setIsUploading(true);
      dispatch(
        tenantActions.uploadFiles([file], 'certificate-doc', (uploadResponse) => {
          setIsUploading(false);
          if (uploadResponse && uploadResponse.data && uploadResponse.data.length > 0) {
            const rawUrl = uploadResponse.data[0];
            const finalUrl =
              typeof rawUrl === 'string'
                ? rawUrl
                : rawUrl?.openbrixName || rawUrl?.url || '';
            setCertificateUrl(finalUrl);
            if (errors.certificateUrl) {
              setErrors((prev) => ({ ...prev, certificateUrl: undefined }));
            }
          } else {
            showErrorToast('Failed to upload file');
          }
        })
      );
    }
  };

  const handleSubmit = (e) => {
    if (e && e.preventDefault) e.preventDefault();

    const newErrors = {};
    if (!documentName.trim()) {
      newErrors.documentName = 'Document name is required';
    }
    if (!certificateUrl) {
      newErrors.certificateUrl = 'Please upload a document file';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    const docs = [
      {
        ...(selectedItem?.certificate_id ? { certificate_id: selectedItem.certificate_id } : {}),
        certificate_type: documentName.trim(),
        certificate_url: certificateUrl,
      },
    ];

    dispatch(
      tenantActions.saveCer(
        {
          certificates: docs,
          send_to_tenant: false,
          flow_id: flowId,
        },
        flowId,
        (res) => {
          setIsSubmitting(false);
          showSuccesToast(selectedItem ? 'Document updated successfully!' : 'Document uploaded successfully!');
          if (onUploaded) onUploaded();
          handleClose();
        }
      )
    );
  };

  return (
    <View style={styles.card}>
      <Image source={require('./assets/otherDoc.png')} style={styles.image} />

      <Text style={styles.name}>Other Documents</Text>

      <Text style={styles.subHeading}>
        If there are any other documents that you consider important, and should be sent to the tenant(s)
        during the tenancy moving in stage, please add them below.
      </Text>

      <View style={styles.statusContainer}>
        <Text style={documents?.length > 0 ? styles.completed : styles.pending}>
          {documents?.length > 0 ? 'Uploaded' : 'Not Completed'}
        </Text>
      </View>

      <div
        style={{
          display: layoutType === 'phone' ? 'block' : 'flex',
          marginTop: '15px',
        }}
      >
        <Button
          type="stripe"
          title="Upload"
          style={{
            marginBottom: 10,
            marginRight: 20,
          }}
          onClick={() => {
            setSelectedItem(null);
            setDocumentName('');
            setUploadedFile(null);
            setCertificateUrl('');
            setErrors({});
            setOpen(true);
          }}
        />
      </div>

      {/* Documents List */}
      {documents &&
        documents.length > 0 &&
        documents.map((item, index) => (
          <CertList
            key={item.certificate_id || index}
            certificate={{ name: item.certificate_type || 'Document' }}
            certificatesItem={item}
            handleEdit={() => handleEdit(item)}
            handleDelete={() => {
              if (handleDelete) {
                handleDelete(item);
              }
            }}
          />
        ))}

      {/* Dialog: Upload / Edit Document */}
      <Dialog
        open={open}
        onClose={() => {
          if (!isUploading && !isSubmitting) handleClose();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle style={{ textAlign: 'center', fontWeight: '700', color: '#1E293B' }}>
          {selectedItem ? 'Edit Document' : 'Upload Document'}
        </DialogTitle>
        <DialogContent>
          <form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <div style={{ marginTop: 12, marginBottom: 18 }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                label="Name"
                placeholder="Document Name (e.g. Tenancy Addendum)"
                value={documentName}
                onChange={(e) => {
                  setDocumentName(e.target.value);
                  if (errors.documentName) {
                    setErrors((prev) => ({ ...prev, documentName: undefined }));
                  }
                }}
                error={Boolean(errors.documentName)}
                helperText={errors.documentName}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <Dropzone
                onDrop={onDropFile}
                accept="image/*,application/pdf,.doc,.docx,.odt,.xls,.xlsx,.txt"
                multiple={false}
              >
                {({ getRootProps, getInputProps, isDragActive }) => (
                  <div
                    {...getRootProps({
                      style: {
                        ...baseDropzoneStyle,
                        borderColor: isDragActive ? '#2E90FA' : errors.certificateUrl ? '#EF4444' : '#CBD5E1',
                        backgroundColor: isDragActive ? '#EFF8FF' : '#F8FAFC',
                      },
                    })}
                  >
                    <input {...getInputProps()} />
                    {isUploading ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                        <CircularProgress size={26} color="primary" />
                        <Typography variant="caption" style={{ color: '#64748B' }}>
                          Uploading file...
                        </Typography>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                        <CloudUploadIcon style={{ fontSize: 28, color: '#2E90FA' }} />
                        <Typography variant="body2" style={{ color: '#334155', fontSize: '13px', fontWeight: 500 }}>
                          Drag 'n' drop or click here to select a file
                        </Typography>
                        <Typography variant="caption" style={{ color: '#94A3B8', fontSize: '11px' }}>
                          PDF, DOC, DOCX, Images up to 25MB
                        </Typography>
                      </div>
                    )}
                  </div>
                )}
              </Dropzone>
              {errors.certificateUrl && (
                <Typography variant="caption" style={{ color: '#EF4444', marginTop: 4, display: 'block' }}>
                  {errors.certificateUrl}
                </Typography>
              )}
            </div>

            {uploadedFile && (
              <div
                style={{
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #E2E8F0',
                  borderRadius: 8,
                  padding: '10px 14px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 16,
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', maxWidth: '80%' }}>
                  <Typography variant="body2" style={{ fontWeight: 600, color: '#1E293B', fontSize: '13px' }}>
                    {uploadedFile.name || 'Selected Document'}
                  </Typography>
                  <Typography variant="caption" style={{ color: '#10B981', fontSize: '11px' }}>
                    {isUploading ? 'Uploading...' : 'Ready to submit'}
                  </Typography>
                </div>
                {certificateUrl && (
                  <Tooltip title="View Certificate">
                    <IconButton
                      size="small"
                      onClick={() => window.open(uploadedFile.preview || certificateUrl, '_blank')}
                      style={{ color: '#0288D1' }}
                    >
                      <VisibilityIcon style={{ fontSize: 20 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </div>
            )}
          </form>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', justifyContent: 'center' }}>
          <MuiButton
            onClick={handleClose}
            variant="outlined"
            disabled={isSubmitting || isUploading}
            style={{ textTransform: 'none', marginRight: 12, minWidth: 100 }}
          >
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting || isUploading || !certificateUrl}
            style={{
              textTransform: 'none',
              backgroundColor: '#8c57ff',
              color: '#FFFFFF',
              minWidth: 100,
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : selectedItem ? (
              'Update'
            ) : (
              'Submit'
            )}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    width: layoutType === 'web' ? '48%' : '100%',
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 15,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOpacity: 0.1,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 4,
    elevation: 5,
  },
  image: {
    width: '100%',
    height: 100,
    borderRadius: 10,
    marginBottom: 10,
  },
  name: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  subHeading: {
    fontSize: 14,
    color: '#555',
    marginBottom: 5,
  },
  statusContainer: {
    marginVertical: 10,
  },
  completed: {
    color: '#4CAF50',
    fontWeight: 'bold',
  },
  pending: {
    color: '#FF9800',
    fontWeight: 'bold',
  },
});

export default OtherDocuments;

